import ApiCache from '@/classes/api-cache';
import FilterGroupFilterCombi from '@/classes/filter-group-filter-combi';
import State from '@/classes/state';
import DealerCollection from '@/models/dealer-collection.model';
import { DealerDetails } from '@/models/dealer-details.model';
import Dealer from '@/models/dealer.model';
import Place from '@/models/place.model';
import axios, { AxiosResponse } from 'axios';
import { LatLng, Map as LeafletMap } from 'leaflet';

const apiCache = new ApiCache();

const actionUtils = {
    appendFiltersToUrl(
        url: string,
        filters: Array<FilterGroupFilterCombi>,
        additionalParams: Array<string>,
    ): string {
        const queryParams = [...additionalParams];
        let resultUrl = url;

        if (filters.length) {
            const filterParams: Record<string, Array<string>> = {};

            filters.forEach(filterGroupFilterCombi => {
                const name = filterGroupFilterCombi.filterGroup.name;

                if (!filterParams[name]) {
                    filterParams[name] = [];
                }

                filterParams[name].push(filterGroupFilterCombi.filter.value);
            });

            Object.keys(filterParams).forEach(param => {
                queryParams.push(`${param}[]=${filterParams[param].join(',')}`);
            });
        }

        if (queryParams.length) {
            resultUrl += `${resultUrl.indexOf('?') === -1 ? '?' : '&'}${queryParams.join('&')}`;
        }

        return resultUrl;
    },
    calculateDistancesAndSortDealers(state: State, dealers: Array<Dealer>): Array<Dealer> {
        const place = state.myPlace;

        if (!place) {
            return dealers;
        }

        const config = state.config;
        const latLng = new LatLng(place.lat, place.lng);

        dealers.forEach(dealer => {
            const distanceInMeters = latLng.distanceTo([dealer.geometry.coordinates[1], dealer.geometry.coordinates[0]]);
            const calculatedDistance = distanceInMeters * config.map.distanceMultiply / 1000;
            dealer.properties.distance = calculatedDistance;
        });

        dealers.sort((dealerA, dealerB) => {
            // The dealers are sorted in the following order:
            //  1. Sort value DESC (configured in CMS (cat2))
            //  2. Distance ASC (if sort value is equal)
            //  3. Name ASC (if sort value and distance is equal)

            if (dealerA.properties.sort === dealerB.properties.sort) {
                if (dealerA.properties.distance === dealerB.properties.distance) {
                    return dealerA.properties.name1.localeCompare(dealerB.properties.name1);
                }

                return dealerA.properties.distance - dealerB.properties.distance;
            }

            return dealerB.properties.sort - dealerA.properties.sort
        });

        return dealers;
    },
};

const actions = {
    /**
     * Selects the given filter
     * @param store The Vuex store
     * @param filterGroupFilterCombi - The filterGroup-filter combination
     */
    selectFilter(store: any, filterGroupFilterCombi: FilterGroupFilterCombi): void {
        store.commit('SELECT_FILTER', filterGroupFilterCombi);
        store.dispatch('replaceState');
    },
    /**
     * Unselects the given filter
     * @param store The Vuex store
     * @param filterGroupFilterCombi - The filterGroup-filter combination
     */
    unselectFilter(store: any, filterGroupFilterCombi: FilterGroupFilterCombi): void {
        store.commit('UNSELECT_FILTER', filterGroupFilterCombi);
        store.dispatch('replaceState');
    },
    /**
     * Sets the given searchterm
     * @param store The Vuex store
     * @param searchTerm - The searchterm
     */
    setSearchTerm(store: any, searchTerm: string): void {
        store.commit('SET_SEARCH_TERM', searchTerm);
    },
    async loadMinDealersByPlace(store: any): Promise<void> {
        const state: State = store.state;
        const place = state.myPlace;
        const map = state.map;

        // console.log('loadMinDealersByPlace', place, map);

        if (!map) {
            throw new Error('Map not available in loadMinDealersByPlace');
        }

        if (!place) {
            throw new Error('Place not available in loadMinDealersByPlace');
        }

        const config = state.config;
        const { baseUrl, project, maxDistance, country, minCount } = config.api;
        const selectedFilters = state.selectedFilters;
        const queryParams: Array<string> = [
            `maxdistance=${maxDistance}`,
            `country=${country}`,
        ];

        if (selectedFilters.length) {
            const [op1, op2] = config.api.op;

            if (op1) {
                queryParams.push(`op1=${op1}`);
            }

            if (op2) {
                queryParams.push(`op2=${op2}`);
            }
        }

        const url = actionUtils.appendFiltersToUrl(
            `${baseUrl}${project}/bounding/${place.lat}/${place.lng}/${minCount}`,
            selectedFilters,
            queryParams
        );

        let response: AxiosResponse<DealerCollection>;

        if (apiCache.has(url)) {
            response = (await apiCache.get(url)) as AxiosResponse<DealerCollection>;
        } else {
            const promise = axios.get<DealerCollection>(url);
            apiCache.set(url, promise);

            try {
                response = await promise;
            } catch (error) {
                console.log('Error loading dealers', error);
                return;
            }
        }

        const { bbox } = response.data;

        // console.log('fit bounds', bbox);
        map.fitBounds([[bbox[0], bbox[1]], [bbox[2], bbox[3]]]);
    },
    /**
     * Loads dealers so that the map
     * is filled with markers
     * @param store The Vuex store
     */
    async loadDealersByMapBounds(store: any): Promise<void> {
        const state: State = store.state;
        const map = state.map;

        if (!map) {
            throw new Error('Map not available in loadDealersByMapBounds');
        }

        const config = state.config;
        const { baseUrl, project, maxDistance, country } = config.api;
        const bounds = map.getBounds();
        const southWest = bounds.getSouthWest();
        const northEast = bounds.getNorthEast();
        const selectedFilters = state.selectedFilters;
        const queryParams: Array<string> = [
            `maxdistance=${maxDistance}`,
            `country=${country}`,
        ];

        if (selectedFilters.length) {
            const [op1, op2] = config.api.op;

            if (op1) {
                queryParams.push(`op1=${op1}`);
            }

            if (op2) {
                queryParams.push(`op2=${op2}`);
            }
        }

        const url = actionUtils.appendFiltersToUrl(
            `${baseUrl}${project}/dealer/${southWest.lat}/${southWest.lng}/${northEast.lat}/${northEast.lng}`,
            selectedFilters,
            queryParams
        );

        let response: AxiosResponse<DealerCollection>;

        if (apiCache.has(url)) {
            response = (await apiCache.get(url)) as AxiosResponse<DealerCollection>;
        } else {
            const promise = axios.get<DealerCollection>(url);
            apiCache.set(url, promise);

            try {
                response = await promise;
            } catch (error) {
                console.log('Error loading dealers', error);
                return;
            }
        }

        const dealers = actionUtils.calculateDistancesAndSortDealers(state, response.data.features);
        store.commit('SET_DEALERS', dealers);
    },
    /**
     * Sets the map zoom
     * @param store The Vuex store
     * @param payload The payload
     */
    setMapZoom(store: any, zoom: number): void {
        store.commit('SET_MAP_ZOOM', zoom);
    },
    /**
     * Sets the map center
     * @param store The Vuex store
     * @param centerAndZoom The map center and optional zoom
     */
    setMapCenter(store: any, centerAndZoom: { center: [number, number], zoom?: number }): void {
        const { center, zoom } = centerAndZoom;

        store.commit('SET_MAP_CENTER', center);

        if (typeof zoom === 'number') {
            store.commit('SET_MAP_ZOOM', zoom);
        }
    },
    /**
     * Sets the map object
     * @param store The Vuex store
     * @param map The map object
     */
    setMap(store: any, map: LeafletMap): void {
        store.commit('SET_MAP', map);
    },
    /**
     * Pushes the current state
     * to the history
     */
    replaceState(store: any): void {
        const state: State = store.state;
        const urlParams: Array<[string, string]> = [];

        if (state.myPlace) {
            urlParams.push(['place', `${state.myPlace.lat},${state.myPlace.lng}`]);
        }

        // add searchterm
        if (state.searchTerm) {
            urlParams.push(['term', state.searchTerm]);
        }

        // add filters
        if (state.selectedFilters.length) {
            const filterParams: {
                [x: string]: Array<string>;
            } = {};

            state.selectedFilters.forEach(filterGroupFilterCombi => {
                const name = filterGroupFilterCombi.filterGroup.name;

                if (!filterParams[name]) {
                    filterParams[name] = [];
                }

                filterParams[name].push(filterGroupFilterCombi.filter.value);
            });

            Object.keys(filterParams).forEach(param => {
                urlParams.push([param, filterParams[param].join(',')]);
            });
        }

        const url = `?${urlParams.map(param => `${param[0]}=${encodeURIComponent(param[1])}`).join('&')}`;

        if (url === state.stateUrl) {
            return;
        }

        history.replaceState({}, '', url)
        store.commit('SET_STATE_URL', url);
    },
    /**
     * Sets the showMap state
     * @param store The Vuex store
     * @param formIsLoading Boolean state
     */
    setShowMap(store: any, showMap: boolean): void {
        store.commit('SET_SHOW_MAP', showMap);
    },
    /**
     * Sets the mapInitialized state
     * @param store The Vuex store
     * @param mapInitialized Boolean state
     */
    setMapInitialized(store: any, mapInitialized: boolean): void {
        store.commit('SET_MAP_INITIALIZED', mapInitialized);
    },
    /**
     * Sets the users place
     * @param store The Vuex store
     * @param myPlace The place
     */
    setMyPlace(store: any, myPlace: Place): void {
        store.commit('SET_MY_PLACE', myPlace);
    },
    /**
     * Sets the active marker id
     * @param store The Vuex store
     * @param activeMarkerId The marker id
     */
    setActiveMarkerId(store: any, activeMarkerId: string | null): void {
        store.commit('SET_ACTIVE_MARKER_ID', activeMarkerId);
    },
    /**
     * Sets the active dealer details
     * @param store The Vuex store
     * @param activeDealerId The dealer details
     */
    setActiveDealerDetails(store: any, activeDealerDetails: DealerDetails | null): void {
        store.commit('SET_ACTIVE_DEALER_DETAILS', activeDealerDetails);
    },
    /**
     * Sets the dealerListOpen state
     * @param store The Vuex store
     * @param dealerListOpen Boolean state
     */
     setDealerListOpen(store: any, dealerListOpen: boolean): void {
        store.commit('SET_DEALER_LIST_OPEN', dealerListOpen);
    },
};

export default actions;
